import React, { useState, useEffect } from 'react';
import { Scenario, ScenarioComparisonRequest } from '../../types/sop';
import { SOPService } from '../../services/sopApi';
import TimeSeriesChart from './TimeSeriesChart';
import LoadingIndicator from '../common/LoadingIndicator';
import ErrorDisplay from '../common/ErrorDisplay';
import './ScenarioComparison.css';

interface ScenarioComparisonProps {
  scenarioIds: string[];
  scenarios: Scenario[];
  onBack: () => void;
}

interface ComparisonData {
  scenario_id: string;
  scenario_name: string;
  metrics: Record<string, number>;
}

const ScenarioComparison: React.FC<ScenarioComparisonProps> = ({
  scenarioIds,
  scenarios,
  onBack
}) => {
  const [comparisonData, setComparisonData] = useState<ComparisonData[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([
    'total_inventory',
    'total_inbound',
    'total_outbound',
    'stockout_risk'
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadComparisonData();
  }, [scenarioIds, selectedMetrics]);

  const loadComparisonData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const request: ScenarioComparisonRequest = {
        scenario_ids: scenarioIds,
        metrics: selectedMetrics
      };
      
      const response = await SOPService.compareScenarios(request);
      setComparisonData(response.comparison_data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comparison data');
    } finally {
      setLoading(false);
    }
  };

  const getScenarioDetails = (scenarioId: string) => {
    return scenarios.find(s => s.scenario_id === scenarioId);
  };

  const availableMetrics = [
    { key: 'total_inventory', label: 'Total Inventory', unit: 'units' },
    { key: 'total_inbound', label: 'Total Inbound', unit: 'units' },
    { key: 'total_outbound', label: 'Total Outbound', unit: 'units' },
    { key: 'stockout_risk', label: 'Stockout Risk', unit: '%' },
    { key: 'overstock_risk', label: 'Overstock Risk', unit: '%' },
    { key: 'total_cost', label: 'Total Cost', unit: '$' },
    { key: 'service_level', label: 'Service Level', unit: '%' }
  ];

  const handleMetricToggle = (metricKey: string) => {
    setSelectedMetrics(prev => {
      if (prev.includes(metricKey)) {
        return prev.filter(m => m !== metricKey);
      } else {
        return [...prev, metricKey];
      }
    });
  };

  const formatMetricValue = (value: number, unit: string) => {
    if (unit === '$') {
      return `$${value.toLocaleString()}`;
    } else if (unit === '%') {
      return `${(value * 100).toFixed(1)}%`;
    } else {
      return value.toLocaleString();
    }
  };

  const getBestScenario = (metricKey: string) => {
    if (comparisonData.length === 0) return null;
    
    // Determine if higher or lower is better for this metric
    const lowerIsBetter = ['stockout_risk', 'overstock_risk', 'total_cost'].includes(metricKey);
    
    return comparisonData.reduce((best, current) => {
      const currentValue = current.metrics[metricKey] || 0;
      const bestValue = best.metrics[metricKey] || 0;
      
      if (lowerIsBetter) {
        return currentValue < bestValue ? current : best;
      } else {
        return currentValue > bestValue ? current : best;
      }
    });
  };

  const generateChartData = () => {
    return selectedMetrics.map(metric => {
      const metricInfo = availableMetrics.find(m => m.key === metric);
      return {
        name: metricInfo?.label || metric,
        data: comparisonData.map(scenario => ({
          x: scenario.scenario_name,
          y: scenario.metrics[metric] || 0,
          label: scenario.scenario_name
        })),
        color: getMetricColor(metric),
        type: 'bar' as const
      };
    });
  };

  const getMetricColor = (metric: string) => {
    const colors = {
      total_inventory: '#2196F3',
      total_inbound: '#4CAF50',
      total_outbound: '#FF9800',
      stockout_risk: '#F44336',
      overstock_risk: '#FF5722',
      total_cost: '#9C27B0',
      service_level: '#00BCD4'
    };
    return colors[metric as keyof typeof colors] || '#757575';
  };

  if (loading) {
    return <LoadingIndicator message="Comparing scenarios..." />;
  }

  if (error) {
    return (
      <ErrorDisplay 
        error={error} 
        onRetry={loadComparisonData}
        operation="Scenario Comparison"
      />
    );
  }

  return (
    <div className="scenario-comparison">
      <div className="comparison-header">
        <h2>Scenario Comparison</h2>
        <p>Compare {scenarioIds.length} scenarios across key metrics</p>
      </div>

      <div className="comparison-controls">
        <div className="metric-selector">
          <h4>Select Metrics to Compare:</h4>
          <div className="metric-checkboxes">
            {availableMetrics.map(metric => (
              <label key={metric.key} className="metric-checkbox">
                <input
                  type="checkbox"
                  checked={selectedMetrics.includes(metric.key)}
                  onChange={() => handleMetricToggle(metric.key)}
                />
                <span>{metric.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="comparison-content">
        <div className="comparison-table">
          <h3>Detailed Comparison</h3>
          <div className="table-container">
            <table className="comparison-data-table">
              <thead>
                <tr>
                  <th>Scenario</th>
                  {selectedMetrics.map(metric => {
                    const metricInfo = availableMetrics.find(m => m.key === metric);
                    return (
                      <th key={metric}>
                        {metricInfo?.label}
                        <span className="metric-unit">({metricInfo?.unit})</span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {comparisonData.map(scenario => {
                  const scenarioDetails = getScenarioDetails(scenario.scenario_id);
                  return (
                    <tr key={scenario.scenario_id}>
                      <td className="scenario-name-cell">
                        <div className="scenario-name">{scenario.scenario_name}</div>
                        {scenarioDetails?.ai_generated && (
                          <span className="ai-badge">AI</span>
                        )}
                      </td>
                      {selectedMetrics.map(metric => {
                        const metricInfo = availableMetrics.find(m => m.key === metric);
                        const value = scenario.metrics[metric] || 0;
                        const bestScenario = getBestScenario(metric);
                        const isBest = bestScenario?.scenario_id === scenario.scenario_id;
                        
                        return (
                          <td 
                            key={metric} 
                            className={`metric-cell ${isBest ? 'best-value' : ''}`}
                          >
                            {formatMetricValue(value, metricInfo?.unit || '')}
                            {isBest && <span className="best-indicator">★</span>}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="comparison-charts">
          <h3>Visual Comparison</h3>
          <div className="charts-grid">
            {selectedMetrics.map(metric => {
              const metricInfo = availableMetrics.find(m => m.key === metric);
              const chartData = [{
                name: metricInfo?.label || metric,
                data: comparisonData.map(scenario => ({
                  x: scenario.scenario_name,
                  y: scenario.metrics[metric] || 0,
                  label: scenario.scenario_name
                })),
                color: getMetricColor(metric),
                type: 'bar' as const
              }];

              return (
                <div key={metric} className="chart-container">
                  <h4>{metricInfo?.label} Comparison</h4>
                  <TimeSeriesChart
                    data={comparisonData.map(s => ({
                      week_date: s.scenario_name,
                      total_inbound: s.metrics.total_inbound || 0,
                      total_outbound: s.metrics.total_outbound || 0,
                      total_inventory: s.metrics.total_inventory || 0,
                      warehouse_count: 1
                    }))}
                    metrics={[metric]}
                    colors={[getMetricColor(metric)]}
                    title={metricInfo?.label || metric}
                    width={350}
                    height={250}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="comparison-insights">
          <h3>Key Insights</h3>
          <div className="insights-grid">
            {selectedMetrics.map(metric => {
              const metricInfo = availableMetrics.find(m => m.key === metric);
              const bestScenario = getBestScenario(metric);
              const values = comparisonData.map(s => s.metrics[metric] || 0);
              const avgValue = values.reduce((sum, val) => sum + val, 0) / values.length;
              const bestValue = bestScenario?.metrics[metric] || 0;
              const improvement = Math.abs((bestValue - avgValue) / avgValue * 100);

              return (
                <div key={metric} className="insight-card">
                  <h4>{metricInfo?.label}</h4>
                  <div className="insight-content">
                    <div className="best-scenario">
                      <strong>Best:</strong> {bestScenario?.scenario_name}
                    </div>
                    <div className="improvement">
                      <strong>Improvement:</strong> {improvement.toFixed(1)}% vs average
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="comparison-actions">
        <button onClick={onBack} className="btn btn-secondary">
          Back to Scenarios
        </button>
        <button 
          onClick={() => window.print()} 
          className="btn btn-primary"
        >
          Export Report
        </button>
      </div>
    </div>
  );
};

export default ScenarioComparison;